/**
 * Review handoff 2026-06-09 §7 — read-only Services section on the order
 * detail ("quote") view.
 *
 * Since BE develop `9162ec10` the canonical source is the top-level
 *   orderServiceManuals: [{ manualId, manual: { id, title, ... },
 *     pricePerSquare, pricePerBuilding, totalPrice }]
 * junction — prices are snapshotted per service at order creation
 * (adjustment resolved per building input format), the line charge is
 * `totalPrice`.
 *
 * The order read ALSO still nests every job manual under
 * jobs[].jobJobStatusInputFormatManuals[].manual (services = the subset
 * with manual.isServiceType, repeated on every job). That shape carries no
 * price; it remains only as the extraction fallback for older payloads.
 *
 * Row icons reflect the ORDER's input format — read from
 * buildings[].inputFormat like ModelsView/SpecificationView do — the same
 * set for every service (one distinct type → its glyph, several → mixed,
 * none → placeholder). Every read must tolerate missing keys, nulls and
 * shape variations without throwing.
 */

import { List } from "immutable";

// Probed in priority order — first key holding a non-empty list wins.
// orderServiceManuals is the live BE contract; the rest are legacy probes.
const SERVICE_KEYS = [
	"orderServiceManuals",
	"orderManuals",
	"orderServices",
	"services",
	"manuals",
];

/**
 * Reads a key from either an Immutable Map or a plain object.
 */
const readKey = (item, key) => {
	if (!item) return undefined;
	if (typeof item.get === "function") return item.get(key);
	if (typeof item === "object") return item[key];
	return undefined;
};

/**
 * Coerces a value to a finite number, or null.
 * Mirrors the exports quirk where an unset customPrice arrives as
 * null/object — anything non-numeric (objects, Maps, booleans, empty or
 * non-numeric strings) resolves to null instead of NaN/0.
 */
const toFiniteNumber = (value) => {
	if (typeof value === "number") {
		return Number.isFinite(value) ? value : null;
	}
	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
};

/**
 * Normalizes one raw service item (junction or bare manual shape, Immutable
 * or plain) to { id, title, price, content } — or null when the item itself
 * is null. Price resolution: customPrice (exports-style override) → price →
 * totalPrice (the order_service_manual snapshot). `content` is the manual's
 * raw Lexical-JSON description (leftJoinAndSelect on order_service_manual.manual
 * selects every Manual column, content included) — null when absent.
 */
const normalizeServiceItem = (item) => {
	if (item === null || item === undefined) return null;

	const manual = readKey(item, "manual");

	const id =
		readKey(manual, "id") ??
		readKey(item, "manualId") ??
		readKey(item, "id") ??
		null;
	const title = readKey(manual, "title") ?? readKey(item, "title") ?? null;
	const content =
		readKey(manual, "content") ?? readKey(item, "content") ?? null;

	const customPrice = toFiniteNumber(readKey(item, "customPrice"));
	const price =
		customPrice !== null
			? customPrice
			: toFiniteNumber(readKey(item, "price")) ??
				toFiniteNumber(readKey(item, "totalPrice"));

	return { id, title, price, content };
};

/**
 * Coerces a value to a plain array of items, accepting Immutable Lists and
 * plain arrays only (a Map.toArray() would yield [key, value] tuples in
 * Immutable 4 — must not be accepted). Returns null otherwise.
 */
const toItemArray = (value) => {
	if (List.isList(value)) return value.toArray();
	if (Array.isArray(value)) return value;
	return null;
};

/**
 * Resolves an item's nested inputFormat to its top-level type name
 * ("CAD" / "Image" / "Point cloud"), preferring the parent like ExportsView
 * does for export types. Returns null when absent.
 */
const readInputFormatType = (item) => {
	const inputFormat = readKey(item, "inputFormat");
	if (!inputFormat) return null;

	const parent = readKey(inputFormat, "parent");
	const type = readKey(parent, "type") ?? readKey(inputFormat, "type") ?? null;

	return typeof type === "string" && type.trim() !== "" ? type : null;
};

/**
 * Distinct input format type names of the ORDER, read from
 * buildings[].inputFormat — the same source ModelsView and SpecificationView
 * use on the single view. Drives the row icon for every service.
 */
const getOrderInputFormatTypes = (project) => {
	const buildings = toItemArray(readKey(project, "buildings")) || [];
	const types = [];

	for (const building of buildings) {
		const formatType = readInputFormatType(building);
		if (formatType && !types.includes(formatType)) {
			types.push(formatType);
		}
	}

	return types;
};

/**
 * Extracts services from the persisted-order shape
 * (jobs[].jobJobStatusInputFormatManuals[].manual). Only manuals flagged
 * isServiceType qualify — jobs also carry the default (non-service) manuals.
 * The same service is attached to every job of the order, so results are
 * deduped by manual id (falling back to title for id-less rows). Icons are
 * NOT derived here — they are order-level (see getOrderInputFormatTypes).
 */
const getServicesFromJobs = (project) => {
	const jobs = toItemArray(readKey(project, "jobs"));
	if (!jobs) return [];

	const seen = new Set();
	const services = [];

	for (const job of jobs) {
		const junctions =
			toItemArray(readKey(job, "jobJobStatusInputFormatManuals")) || [];

		for (const junction of junctions) {
			const manual = readKey(junction, "manual");
			if (!manual || !readKey(manual, "isServiceType")) continue;

			const normalized = normalizeServiceItem(junction);
			if (!normalized) continue;

			const dedupeKey =
				normalized.id != null
					? `id:${normalized.id}`
					: `title:${normalized.title}`;

			if (seen.has(dedupeKey)) continue;
			seen.add(dedupeKey);
			services.push(normalized);
		}
	}

	return services;
};

/**
 * Tolerant accessor for services attached to an order.
 *
 * @param {Immutable.Map|null|undefined} project - order data from
 *   state.getIn(["project", "project"])
 * @returns {Array<{id: *, title: string|null, price: number|null,
 *   content: string|null, inputFormatTypes: string[]}>} plain JS array — []
 *   when the project or every probed key is missing/empty/invalid
 */
export const getOrderServices = (project) => {
	if (!project || typeof project !== "object") return [];

	// One format set for the whole order — every row shows the same icon.
	const inputFormatTypes = getOrderInputFormatTypes(project);
	const withTypes = (service) => ({ ...service, inputFormatTypes });

	for (const key of SERVICE_KEYS) {
		const items = toItemArray(readKey(project, key));

		// A present array is authoritative even when EMPTY — after removing the
		// last service the BE returns orderServiceManuals: [], while the jobs
		// still carry the manual under jobJobStatusInputFormatManuals (job-level
		// assignment, not deleted with the order-level row). Falling back to the
		// jobs shape here would resurrect deselected services. Only a MISSING
		// key (older payloads) falls through to getServicesFromJobs.
		if (items) {
			return items
				.map(normalizeServiceItem)
				.filter((service) => service !== null)
				.map(withTypes);
		}
	}

	return getServicesFromJobs(project).map(withTypes);
};

/**
 * Sums the non-null prices of normalized services. Returns 0 when there are
 * none so the PriceSummary subtotal math is unchanged while the BE does not
 * send services.
 *
 * @param {Array<{price: number|null}>|null|undefined} services
 * @returns {number}
 */
export const getOrderServicesTotal = (services) => {
	if (!Array.isArray(services)) return 0;

	return services.reduce(
		(total, service) =>
			service &&
			typeof service.price === "number" &&
			Number.isFinite(service.price)
				? total + service.price
				: total,
		0
	);
};
